/**
 * BancoNxsView v2 -- NXS Bank with team + individual views.
 * Shows balance overview, level progress, reward history,
 * and per-agent/user NXS breakdown.
 *
 * @module presentation/views/BancoNxsView
 */

import { RewardService } from '../../application/RewardService.js';
import { MissionApiClient } from '../../infrastructure/api/MissionApiClient.js';
import { NxsApiClient } from '../../infrastructure/api/NxsApiClient.js';
import { renderNxList, bindNxListEvents, NX_LIST_VIEWER_VERSION } from '../shared/NxListViewer.js';
import { displayNameFor, LEGACY_CODES, normalizeAgentCode } from '../../infrastructure/api/AgentRegistry.js';
import { esc } from '../shared/texto.js';
import { regresoHtml } from '../shared/regreso.js';
import { formatearFecha } from '../shared/fechaColombiana.js';

// ADS-T5: agentes retirados en el banco (mantienen su saldo historico, sin borrar). Incluye
// los LEGACY del registro (ARGYS-*) + ARGEMIRO-CLIENTE-001 (Argy X, retirado del ledger).
const BANCO_LEGACY = new Set([...LEGACY_CODES, 'ARGEMIRO-CLIENTE-001']);
const isBancoLegacy = (code) => BANCO_LEGACY.has(normalizeAgentCode(code));

const LEVEL_NAMES = ['Aprendiz', 'Iniciado', 'Explorador', 'Guardian', 'Maestro'];
const LEVEL_THRESHOLDS = [0, 50, 200, 500, 1000];

const AGENT_CODES = ['ARES-001', 'ARGEMIRO-001', 'ARGYS-001'];

let currentTab = 'team';

export async function renderBancoNxs(container) {
  container.innerHTML = '<p class="empty-state">Cargando Banco NXS...</p>';

  const [board, missionsRes] = await Promise.all([
    RewardService.getLeaderboard(),
    MissionApiClient.list(1, 200)
  ]);

  const allMissions = missionsRes.data?.data?.items || missionsRes.data?.items || [];
  const completed = allMissions
    .filter(m => m.status === 'completed')
    .sort((a, b) => new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt));
  const active = allMissions.filter(m => m.status === 'active' || m.status === 'in_progress');

  const levelName = LEVEL_NAMES[board.level - 1] || `Nivel ${board.level}`;
  const nextThreshold = board.level < 5 ? LEVEL_THRESHOLDS[board.level] : null;
  const currentThreshold = LEVEL_THRESHOLDS[board.level - 1];
  const pendingNxs = active.reduce((sum, m) => sum + (m.rewardNexos || 0), 0);

  // Calculate per-agent NXS from mission descriptions ([AGENTE: ARES-001, ARGEMIRO-001])
  const agentNxs = buildAgentNxs(allMissions);

  container.innerHTML = `
    <section class="artifact-view">
      ${regresoHtml()}

      <div class="banco-header-card">
        <span class="banco-balance">${board.nxs}</span>
        <span class="banco-balance-label">NXS</span>
        <div class="banco-level-info">
          ${levelName} &middot; Nivel ${board.level}
          ${nextThreshold ? ` &middot; Siguiente: ${nextThreshold} NXS` : ' &middot; Nivel maximo'}
        </div>
        <div class="banco-progress-section">
          <div class="progress-bar progress-bar-lg">
            <div class="progress-fill" style="width:${board.progress}%;background:var(--accent-green)"></div>
          </div>
          <div class="banco-progress-labels">
            <span>${currentThreshold} NXS</span>
            <span>${nextThreshold ?? 'MAX'}</span>
          </div>
        </div>
      </div>

      <div class="task-filter-bar" id="nx-banco-tabs" style="margin-top:12px">
        <button class="btn-filter${currentTab === 'team' ? ' active' : ''}" data-banco-tab="team">Equipo</button>
        <button class="btn-filter${currentTab === 'transactions' ? ' active' : ''}" data-banco-tab="transactions">Transacciones</button>
        <button class="btn-filter${currentTab === 'mine' ? ' active' : ''}" data-banco-tab="mine">Mis NXS</button>
        <button class="btn-filter${currentTab === 'history' ? ' active' : ''}" data-banco-tab="history">Historial</button>
      </div>

      <div id="nx-banco-content"></div>
    </section>`;

  const $tabs = document.getElementById('nx-banco-tabs');
  const $content = document.getElementById('nx-banco-content');

  $tabs.addEventListener('click', (e) => {
    const btn = e.target.closest('.btn-filter');
    if (!btn) return;
    $tabs.querySelectorAll('.btn-filter').forEach(b => b.classList.remove('active'));
    btn.classList.add('active');
    currentTab = btn.dataset.bancoTab;
    renderTab($content, currentTab, { board, completed, active, pendingNxs, agentNxs, allMissions });
  });

  renderTab($content, currentTab, { board, completed, active, pendingNxs, agentNxs, allMissions });
}

function renderTab($el, tab, data) {
  if (tab === 'team') renderTeamTab($el, data);
  else if (tab === 'transactions') renderTransactionsTab($el);
  else if (tab === 'mine') renderMyNxsTab($el, data);
  else renderHistoryTab($el, data);
}

async function renderTransactionsTab($el) {
  $el.innerHTML = '<p class="empty-state">Cargando transacciones...</p>';
  const res = await NxsApiClient.getTransactions(null, 100);
  if (!res.ok) {
    $el.innerHTML = `<p class="empty-state">Error: ${res.error}</p>`;
    return;
  }
  const txns = (res.data?.data || res.data || []).map(t => ({
    ...t,
    date: t.created_at || t.createdAt,
    typeLabel: t.type === 'credit' ? '+' : '-',
    amountDisplay: t.type === 'credit' ? `+${t.amount}` : `-${t.amount}`
  }));

  const escHtml = esc; // deuda 4: antes solo cambiaba <
  const listConfig = {
    id: 'nx-nxs-tx-table',
    columns: [
      { key: 'date', label: 'Fecha', width: '15%', render: v => `<span style="font-size:0.875rem">${formatDate(v)}</span>` },
      { key: 'user_id', label: 'Usuario', width: '15%', render: (v) => `<strong style="font-size:0.875rem">${escHtml(v)}</strong>` },
      { key: 'type', label: 'Tipo', width: '8%', render: v => v === 'credit'
        ? '<span style="color:#238636;font-weight:700">credito</span>'
        : '<span style="color:#f85149;font-weight:700">debito</span>' },
      { key: 'amountDisplay', label: 'Monto', width: '10%', render: (v, row) => row.type === 'credit'
        ? `<span style="color:#238636;font-weight:700">${v} NXS</span>`
        : `<span style="color:#f85149;font-weight:700">${v} NXS</span>` },
      { key: 'reference_id', label: 'Referencia', width: '18%', render: v => v ? `<code style="font-size:0.875rem">${escHtml(v)}</code>` : '--' },
      { key: 'description', label: 'Descripcion', render: v => `<span style="font-size:0.875rem;color:#8b949e">${escHtml((v || '').substring(0, 60))}</span>` }
    ],
    data: txns,
    sortBy: 'date',
    sortDir: 'desc',
    emptyMessage: 'Sin transacciones registradas'
  };

  $el.innerHTML = `
    <div class="detail-section">
      <h2>Transacciones NXS</h2>
      <p style="font-size:0.875rem;color:#8b949e;margin-bottom:8px">Registro auditable de creditos y debitos NXS &middot; NxListViewer v${NX_LIST_VIEWER_VERSION}</p>
      ${renderNxList(listConfig)}
    </div>`;

  bindNxListEvents($el, 'nx-nxs-tx-table', listConfig);
}

async function renderTeamTab($el, { agentNxs, board }) {
  // Try API balances first, fallback to mission-calculated
  let teamData = agentNxs;
  try {
    const balRes = await NxsApiClient.getBalances();
    if (balRes.ok && balRes.data?.data?.length > 0) {
      teamData = {};
      for (const b of balRes.data.data) {
        const uid = b.user_id?.trim() || b.userId?.trim();
        if (uid) teamData[uid] = { earned: b.balance || 0, missions: b.credits || 0, pending: 0 };
      }
    }
  } catch { /* fallback to mission-calculated */ }

  // ADS-T5: displayName canonico (no codigos crudos) + legacy vive muerto (fade) para distinguir.
  const teamRow = (code, data, legacy) => {
    const level = calcLevel(data.earned);
    const levelName = LEVEL_NAMES[level - 1];
    const isAgent = AGENT_CODES.includes(code) || isBancoLegacy(code);
    const icon = isAgent ? '\uD83E\uDD16' : '\uD83D\uDC64';
    const name = displayNameFor(code); // canonico; fallback al codigo si no esta registrado
    return `
        <div class="artifact-item" style="display:flex;align-items:center;gap:12px;padding:10px 12px${legacy ? ';opacity:0.72' : ''}">
          <span style="font-size:1.2rem">${icon}</span>
          <div style="flex:1;min-width:0">
            <div style="font-weight:600;font-size:0.88rem">${esc(name)}${legacy ? ' <span style="font-size:0.875rem;color:#8b949e;font-weight:500">\u00B7 retirado</span>' : ''}</div>
            <div style="font-size:0.875rem;color:#6e7681"><code style="font-size:0.875rem">${esc(code)}</code> &middot; ${levelName} &middot; ${data.missions} transaccion${data.missions !== 1 ? 'es' : ''}</div>
          </div>
          <div style="text-align:right">
            <div style="font-weight:700;color:#238636;font-size:0.95rem">${data.earned} NXS</div>
            ${data.pending > 0 ? `<div style="font-size:0.875rem;color:#d29922">+${data.pending} pendientes</div>` : ''}
          </div>
        </div>`;
  };

  const entries = Object.entries(teamData).sort((a, b) => b[1].earned - a[1].earned);
  const activos = entries.filter(([code]) => !isBancoLegacy(code));
  const legacy  = entries.filter(([code]) => isBancoLegacy(code));

  $el.innerHTML = `
    <div class="detail-section">
      <h2>Saldos del Equipo</h2>
      <p style="font-size:0.875rem;color:#8b949e;margin-bottom:8px">Saldos reales desde tabla de transacciones NXS. Cada agente asignado a una mision completada recibe credito.</p>
      <div class="artifact-list">${activos.length > 0 ? activos.map(([c, d]) => teamRow(c, d, false)).join('') : '<p class="empty-state">Sin datos de equipo</p>'}</div>
      ${legacy.length > 0 ? `
        <h3 style="margin:16px 0 6px;font-size:0.875rem;color:#8b949e">Retirados (legacy)</h3>
        <p style="font-size:0.875rem;color:#6e7681;margin-bottom:6px">Agentes previos a los territorios. Conservan su saldo historico; ya no reciben trabajo nuevo.</p>
        <div class="artifact-list">${legacy.map(([c, d]) => teamRow(c, d, true)).join('')}</div>
      ` : ''}
      <div style="margin-top:12px;padding:8px 12px;background:#161b22;border-radius:6px;font-size:0.875rem;color:#6e7681">
        <strong>Total ecosistema:</strong> ${board.nxs} NXS ganados &middot; ${board.completedMissions} misiones completadas
      </div>
    </div>`;
}

function renderMyNxsTab($el, { board, pendingNxs }) {
  $el.innerHTML = `
    <div class="banco-stats-grid">
      <div class="banco-stat-card">
        <div class="banco-stat-value">${board.completedMissions}</div>
        <div class="banco-stat-label">Misiones completadas</div>
      </div>
      <div class="banco-stat-card">
        <div class="banco-stat-value">${board.totalMissions - board.completedMissions}</div>
        <div class="banco-stat-label">Misiones activas</div>
      </div>
      <div class="banco-stat-card">
        <div class="banco-stat-value" style="color:var(--accent-green)">${board.nxs}</div>
        <div class="banco-stat-label">NXS ganados</div>
      </div>
      <div class="banco-stat-card">
        <div class="banco-stat-value" style="color:var(--accent-yellow,#d29922)">${pendingNxs}</div>
        <div class="banco-stat-label">NXS pendientes</div>
      </div>
    </div>
    <div class="detail-section" style="margin-top:12px">
      <p style="font-size:0.875rem;color:#8b949e">Balance personal calculado a partir de misiones completadas. El nivel sube al alcanzar umbrales de NXS.</p>
    </div>`;
}

function renderHistoryTab($el, { completed, active }) {
  $el.innerHTML = `
    <div class="detail-section">
      <h2>Historial de recompensas</h2>
      ${completed.length > 0 ? `
        <table class="banco-history-table">
          <thead><tr><th>Codigo</th><th>Mision</th><th>Agentes</th><th>Fecha</th><th style="text-align:right">NXS</th></tr></thead>
          <tbody>
            ${completed.map(m => {
              const agents = extractAgents(m.description);
              return `<tr>
                <td><a href="#/missions/${m.id}" class="proposal-link"><code class="mission-code">${m.code}</code></a></td>
                <td>${m.title || ''}</td>
                <td style="font-size:0.875rem;color:#8b949e">${agents.join(', ') || '--'}</td>
                <td>${formatDate(m.updatedAt || m.createdAt)}</td>
                <td class="banco-nxs-cell">+${m.rewardNexos || 0}</td>
              </tr>`;
            }).join('')}
          </tbody>
        </table>` : '<p class="empty-state">Aun no hay misiones completadas.</p>'}
    </div>

    ${active.length > 0 ? `
      <div class="detail-section">
        <h2>Misiones en curso</h2>
        <div class="artifact-list">
          ${active.map(m => `
            <a href="#/missions/${m.id}" class="artifact-item mission-link${m.status === 'in_progress' ? ' mission-working' : ''}">
              <div class="task-info">
                <div class="artifact-item-title">
                  ${m.status === 'in_progress' ? '<span class="working-indicator"></span>' : ''}
                  <code class="mission-code">${m.code}</code> ${m.title}
                </div>
                <div class="artifact-item-meta">${m.taskIds?.length || 0} tareas &middot; ${m.rewardNexos || 0} NXS potenciales</div>
              </div>
              <span class="status-badge" style="background:${m.status === 'in_progress' ? '#58a6ff22;color:#58a6ff' : '#23863622;color:#238636'}">${m.status}</span>
            </a>`).join('')}
        </div>
      </div>` : ''}`;
}

// Extract agent codes from mission description [AGENTE: ARES-001, ARGEMIRO-001]
function extractAgents(desc) {
  if (!desc) return [];
  const match = desc.match(/\[AGENTE:\s*([^\]]+)\]/i);
  if (!match) return [];
  return match[1].split(',').map(s => s.trim()).filter(Boolean);
}

// Build NXS breakdown by agent/user from missions
function buildAgentNxs(allMissions) {
  const result = {};
  for (const m of allMissions) {
    const agents = extractAgents(m.description);
    if (agents.length === 0) continue;
    const nxs = m.rewardNexos || 0;
    const isCompleted = m.status === 'completed';
    for (const agent of agents) {
      if (!result[agent]) result[agent] = { earned: 0, pending: 0, missions: 0 };
      if (isCompleted) {
        result[agent].earned += nxs;
        result[agent].missions++;
      } else {
        result[agent].pending += nxs;
      }
    }
  }
  // Add user (nexo-admin) with full earned NXS
  const userEarned = allMissions.filter(m => m.status === 'completed').reduce((s, m) => s + (m.rewardNexos || 0), 0);
  const userPending = allMissions.filter(m => m.status !== 'completed').reduce((s, m) => s + (m.rewardNexos || 0), 0);
  const userMissions = allMissions.filter(m => m.status === 'completed').length;
  if (userEarned > 0 || userPending > 0) {
    result['nexo-admin-001'] = { earned: userEarned, pending: userPending, missions: userMissions };
  }
  return result;
}

function calcLevel(nxs) {
  if (nxs >= 1000) return 5;
  if (nxs >= 500) return 4;
  if (nxs >= 200) return 3;
  if (nxs >= 50) return 2;
  return 1;
}

function formatDate(iso) {
  if (!iso) return '--';
  return formatearFecha(iso, { day: '2-digit', month: 'short', year: 'numeric' });
}
